"use client";

/**
 * Wizard context for the 3-step Configure modal.
 *
 * The shell (`configure-modal.tsx`) owns all wizard state in local React state
 * (step index, picked sequence, waterfall strategy, the credit estimate query,
 * and the "start preview" handoff) and publishes it here so the three step
 * panels (`step-sequence`, `step-enrichment`, `step-review`) can read it without
 * prop-drilling. The persistent cohort payload still lives in
 * `useConfigureStore`; only ephemeral wizard navigation lives in this context.
 */

import { createContext, useContext } from "react";

import type { SequenceListItem } from "@/features/sequences/hooks/use-sequences";

import type { EnrollmentRevealEstimateResponse } from "../../lib/types";
import type { WaterfallStrategy } from "../../lib/types";

export type ConfigureStep = 1 | 2 | 3;

export interface ConfigureWizardValue {
  /** Number of candidates the cohort is enrolling. */
  cohortSize: number;
  /** Current step (1 = Sequence, 2 = Enrichment, 3 = Review). */
  step: ConfigureStep;
  setStep: (step: ConfigureStep) => void;

  /** Job the cohort belongs to (used to deep-link into its sequences page). */
  jobId: string | null;

  // ── Step 1: sequence picker ──
  eligibleSequences: SequenceListItem[];
  /**
   * Whether the job has any sequences at all (any status). Lets the empty state
   * distinguish "no sequences yet, create one" from "sequences exist but none
   * are active, activate one".
   */
  hasAnySequences: boolean;
  sequencesLoading: boolean;
  /** Another page of sequences is available to load. */
  hasMoreSequences: boolean;
  /** A "load more" page fetch is in flight. */
  loadingMoreSequences: boolean;
  /** Fetch the next page of sequences. */
  loadMoreSequences: () => void;
  sequenceId: string | null;
  selectSequence: (id: string) => void;

  // ── Step 2: waterfall enrichment ──
  waterfall: WaterfallStrategy;
  selectWaterfall: (value: WaterfallStrategy) => void;

  // ── Step 3: review (credit estimate) ──
  estimate: EnrollmentRevealEstimateResponse | undefined;
  estimateLoading: boolean;
  estimateError: boolean;
  /** Cohort exceeds the 50-profile enrollment cap. */
  cohortTooLarge: boolean;
  /** Cohort is empty — nothing to enroll. */
  cohortEmpty: boolean;
  /** True when the Preview button must stay disabled (any hard gate). */
  previewDisabled: boolean;
  /** True after a 402 from create — keep the insufficient banner up. */
  insufficientFrom402: boolean;
  /** Kick off the create-preview handoff → progress view. */
  startPreview: () => void;

  /** Close the modal (Cancel / X). */
  onCancel: () => void;
}

const ConfigureWizardContext = createContext<ConfigureWizardValue | null>(null);

export function useConfigureWizard(): ConfigureWizardValue {
  const ctx = useContext(ConfigureWizardContext);
  if (!ctx) {
    throw new Error(
      "useConfigureWizard must be used within a ConfigureWizardProvider",
    );
  }
  return ctx;
}

export const ConfigureWizardProvider = ConfigureWizardContext.Provider;
